import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';

const EnsoCircle = ({ className = '' }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M85 50C85 69.33 69.33 85 50 85C30.67 85 15 69.33 15 50C15 30.67 30.67 15 50 15C62 15 72.5 21 79 30"
      stroke="currentColor"
      strokeWidth="3.5"
      strokeLinecap="round"
    />
  </svg>
);

export default function Header() {
  const { lang, setLanguage, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { labelKey: 'nav_about', href: '#about' },
    { labelKey: 'nav_services', href: '#services' },
    { labelKey: 'nav_team', href: '#team' },
    { labelKey: 'nav_portfolio', href: '#portfolio' },
    { labelKey: 'nav_contact', href: '#contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 ${
        scrolled
          ? 'bg-[#0a0a0c]/95 backdrop-blur-md border-b border-[#c9a96e]/10'
          : 'bg-transparent'
      }`}
      style={{ transition: 'background-color 0.5s ease, border-color 0.5s ease' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <a href="#hero" onClick={(e) => scrollTo(e, '#hero')} className="flex items-center gap-2">
            <EnsoCircle className="w-8 h-8 sm:w-10 sm:h-10 text-[#c9a96e]" />
            <span className="text-[#f0ebe3] font-heading text-lg sm:text-xl font-bold tracking-[0.15em]">
              ENSO
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollTo(e, link.href)}
                className="text-[#8a857e] hover:text-[#c9a96e] text-[13px] font-medium tracking-[0.15em] uppercase"
                style={{ transition: 'color 0.3s ease' }}
              >
                {t(link.labelKey)}
              </a>
            ))}
          </nav>

          {/* Right side: Lang toggle + CTA + Mobile Toggle */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language Toggle */}
            <div className="flex items-center border border-[#2a2a30] rounded-lg overflow-hidden">
              <button
                onClick={() => setLanguage('en')}
                className={`px-2.5 py-1.5 text-[11px] font-semibold tracking-wider ${
                  lang === 'en'
                    ? 'bg-[#c9a96e] text-[#0a0a0c]'
                    : 'text-[#6a6560] hover:text-[#8a857e]'
                }`}
                style={{ transition: 'background-color 0.3s ease, color 0.3s ease' }}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('ro')}
                className={`px-2.5 py-1.5 text-[11px] font-semibold tracking-wider ${
                  lang === 'ro'
                    ? 'bg-[#c9a96e] text-[#0a0a0c]'
                    : 'text-[#6a6560] hover:text-[#8a857e]'
                }`}
                style={{ transition: 'background-color 0.3s ease, color 0.3s ease' }}
              >
                RO
              </button>
            </div>

            <Button
              onClick={(e) => scrollTo(e, '#contact')}
              className="hidden sm:flex bg-[#c9a96e] text-[#0a0a0c] hover:bg-[#d4bc8a] font-medium text-xs sm:text-sm tracking-wide h-9 sm:h-10 px-4 sm:px-6"
            >
              <Phone className="w-3.5 h-3.5 mr-1.5" />
              {t('nav_contact_us')}
            </Button>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-[#f0ebe3] hover:text-[#c9a96e] p-1"
              style={{ transition: 'color 0.3s ease' }}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden ${
          mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
        style={{ transition: 'max-height 0.4s ease, opacity 0.3s ease' }}
      >
        <div className="bg-[#0a0a0c]/98 backdrop-blur-md border-t border-[#2a2a30]">
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollTo(e, link.href)}
                className="text-[#8a857e] hover:text-[#c9a96e] text-sm font-medium tracking-[0.1em] uppercase py-3 border-b border-[#1a1a1f]"
                style={{ transition: 'color 0.3s ease' }}
              >
                {t(link.labelKey)}
              </a>
            ))}
            <Button
              onClick={(e) => scrollTo(e, '#contact')}
              className="mt-3 bg-[#c9a96e] text-[#0a0a0c] hover:bg-[#d4bc8a] font-medium w-full h-11"
            >
              <Phone className="w-4 h-4 mr-2" />
              {t('nav_contact_us')}
            </Button>
          </nav>
        </div>
      </div>
    </header>
  );
}
